import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  TableCellsIcon,
  UserIcon,
  CurrencyDollarIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import { getBirds, createBird, updateBird } from '../api/birds';
import type { Bird, CreateBirdDto } from '../api/birds';
import { Gender, BirdStatus } from '../utils/enums';
import AddBirdModal from '../components/AddBirdModal';
import EditBirdModal from '../components/EditBirdModal';
import BirdDetails from '../components/BirdDetails';
import SellBirdModal from '../components/SellBirdModal';
import BirdHealthIndicator from '../components/birds/BirdHealthIndicator';
import ImportBirdCertificateModal from '../components/reports/ImportBirdCertificateModal';
import toast from 'react-hot-toast';
import { getPhotoUrl } from '../api/photos';
import Avatar from '../components/common/Avatar';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';
import {
  BrandModal,
  BrandModalBody,
  BrandModalHeader,
} from '../components/ui/BrandModal';
import { getApiErrorMessage } from '../utils/errorHandling';
import SortOrderButton from '../components/ui/SortOrderButton';
import { getGenderColorClass, getGenderIcon } from '../utils/genderDisplay';
import { daysAgo, type BirdListFilter } from '../utils/listFilterPaths';
import CageSelect from '../components/CageSelect';

interface BirdFilters {
  gender?: Gender;
  status?: BirdStatus;
  speciesId?: string;
  cageNumber?: string;
  focus?: BirdListFilter;
}

export default function Birds() {
  // Data state
  const [birds, setBirds] = useState<Bird[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<BirdFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState<keyof Bird>('bandId');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportCertificateModal, setShowImportCertificateModal] = useState(false);
  const [addBirdDraft, setAddBirdDraft] = useState<CreateBirdDto | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [selectedBird, setSelectedBird] = useState<Bird | null>(null);

  useEffect(() => {
    loadBirds();
  }, []);

  useEffect(() => {
    const focus = searchParams.get('filter') as BirdListFilter | null;
    const status = searchParams.get('status') as BirdStatus | null;
    const next: BirdFilters = {};
    if (focus === 'recentlyHatched' || focus === 'juveniles' || focus === 'attention') {
      next.focus = focus;
    }
    if (status && Object.values(BirdStatus).includes(status)) {
      next.status = status;
    }
    if (Object.keys(next).length > 0) {
      setFilters(next);
      setShowFilters(true);
      if (focus === 'recentlyHatched' || focus === 'juveniles') {
        setSortBy('hatchingDate');
        setSortOrder('desc');
      }
    }
  }, [searchParams]);

  const loadBirds = async () => {
    try {
      setLoading(true);
      const birdsData = await getBirds();
      setBirds(Array.isArray(birdsData) ? birdsData : []);
      setError(null);
    } catch (err) {
      setError('Failed to load birds');
      console.error('Error loading birds:', err);
      toast.error('Failed to load birds');
    } finally {
      setLoading(false);
    }
  };

  // Filter and search logic
  const filteredBirds = useMemo(() => {
    let result = birds;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (bird) =>
          bird.bandId.toLowerCase().includes(term) ||
          bird.species?.commonName?.toLowerCase().includes(term) ||
          bird.species?.genus?.toLowerCase().includes(term) ||
          bird.species?.species?.toLowerCase().includes(term) ||
          bird.mutationName?.toLowerCase().includes(term) ||
          bird.cageNumber.toLowerCase().includes(term) ||
          bird.phenotype?.toLowerCase().includes(term) ||
          bird.genotype?.toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (filters.gender) {
      result = result.filter((bird) => bird.gender === filters.gender);
    }
    if (filters.status) {
      result = result.filter((bird) => bird.status === filters.status);
    }
    if (filters.speciesId) {
      result = result.filter((bird) => bird.species?.id === filters.speciesId);
    }
    if (filters.cageNumber) {
      result = result.filter((bird) => bird.cageNumber === filters.cageNumber);
    }
    if (filters.focus === 'recentlyHatched') {
      const cutoff = daysAgo(30);
      result = result.filter((bird) => bird.hatchingDate && new Date(bird.hatchingDate) > cutoff);
    } else if (filters.focus === 'juveniles') {
      const cutoff = daysAgo(90);
      result = result.filter((bird) => bird.hatchingDate && new Date(bird.hatchingDate) > cutoff);
    } else if (filters.focus === 'attention') {
      result = result.filter(
        (bird) =>
          !bird.avatarPhotoId ||
          bird.status === BirdStatus.SICK ||
          bird.status === BirdStatus.DECEASED,
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      // Handle nested properties
      if (sortBy === 'species' && a.species && b.species) {
        aVal = a.species.commonName;
        bVal = b.species.commonName;
      }
      
      // Handle undefined values
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return sortOrder === 'asc' ? -1 : 1;
      if (bVal === undefined) return sortOrder === 'asc' ? 1 : -1;
      
      // Convert to strings for comparison
      const aStr = String(aVal);
      const bStr = String(bVal);
      
      if (aStr < bStr) return sortOrder === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [birds, searchTerm, filters, sortBy, sortOrder]);

  // Pagination logic
  const totalPages = Math.ceil(filteredBirds.length / itemsPerPage);
  const paginatedBirds = filteredBirds.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get unique values for filter options
  const uniqueSpecies = [...new Set(birds.map((bird) => bird.species?.id).filter((id): id is string => Boolean(id)))];
  const speciesMap = new Map(birds.filter(b => b.species).map(b => [b.species!.id, b.species!]));

  // Event handlers
  const handleAddBird = async (birdData: CreateBirdDto) => {
    try {
      const newBird = await createBird(birdData);
      setBirds(prev => [...prev, newBird]);
      setShowAddModal(false);
      setAddBirdDraft(null);

      const wantedPurchase =
        birdData.provenance?.type === 'BOUGHT' &&
        Boolean(birdData.provenance.price && birdData.provenance.price > 0) &&
        Boolean(birdData.provenance.sellerName);

      if (wantedPurchase) {
        if (newBird.provenance?.purchaseId) {
          toast.success('Bird added and purchase recorded');
        } else {
          toast.success('Bird added');
          toast.error(
            'Purchase entry was not created — add it manually from Purchases if needed',
          );
        }
      } else {
        toast.success('Bird added successfully');
      }
    } catch (err) {
      console.error('Error adding bird:', err);
      toast.error(getApiErrorMessage(err, 'Failed to add bird'));
    }
  };

  const openAddBird = () => {
    setAddBirdDraft(null);
    setShowAddModal(true);
  };

  const handleCertificateImport = (draft: CreateBirdDto) => {
    setAddBirdDraft(draft);
    setShowAddModal(true);
  };

  const handleEditBird = async (id: string, birdData: Partial<CreateBirdDto>) => {
    try {
      const updatedBird = await updateBird(id, birdData);
      setBirds(prev => prev.map(bird => bird.id === id ? updatedBird : bird));
      setShowEditModal(false);
      setSelectedBird(null);
      toast.success('Bird updated successfully');
    } catch (err) {
      console.error('Error updating bird:', err);
      toast.error(getApiErrorMessage(err, 'Failed to update bird'));
    }
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setCurrentPage(1);
    setSearchParams({});
  };

  const focusLabel =
    filters.focus === 'recentlyHatched'
      ? 'Recently hatched (last 30 days)'
      : filters.focus === 'juveniles'
        ? 'Recent hatchlings (last 90 days)'
        : filters.focus === 'attention'
          ? 'Birds needing attention'
          : filters.status === BirdStatus.BREEDING
            ? 'Birds in breeding status'
            : null;

  const openDetailsModal = (bird: Bird) => {
    setSelectedBird(bird);
    setShowDetailsModal(true);
  };

  const openEditModal = (bird: Bird) => {
    setSelectedBird(bird);
    setShowEditModal(true);
  };

  const openSellModal = (bird: Bird) => {
    setSelectedBird(bird);
    setShowSellModal(true);
  };

  const getStatusBadgeColor = (status: BirdStatus) => {
    switch (status) {
      case BirdStatus.PAIRED:
        return 'bg-emerald-100 text-emerald-800';
      case BirdStatus.SOLD:
        return 'bg-violet-100 text-violet-800';
      case BirdStatus.DECEASED:
        return 'bg-rose-100 text-rose-800';
      default:
        return 'bg-cyan-100 text-cyan-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-red-600 text-center">{error}</div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <PageHero
        title="Birds"
        subtitle="Manage your bird collection with detailed information and photo galleries"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowImportCertificateModal(true)}
              className="btn btn-secondary focus-pop"
            >
              <ArrowUpTrayIcon className="-ml-1 mr-2 h-5 w-5" />
              Import certificate
            </button>
            <button
              type="button"
              onClick={openAddBird}
              className="btn btn-primary focus-pop"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Add Bird
            </button>
          </div>
        }
      />

      {focusLabel && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm text-cyan-900">
          <span>
            Showing: <span className="font-semibold">{focusLabel}</span>
          </span>
          <button type="button" onClick={clearFilters} className="font-medium text-cyan-700 hover:text-cyan-900">
            Clear filter
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="glass-panel mt-6 rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="flex items-center w-full rounded-md border border-gray-300 bg-white focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500">
              <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-sky-500" aria-hidden="true">
                <MagnifyingGlassIcon className="h-5 w-5" />
              </span>
              <input
                type="text"
                className="flex-1 min-w-0 py-2 pr-3 border-0 bg-transparent leading-5 placeholder-gray-500 focus:outline-none focus:ring-0 focus:placeholder-gray-400 sm:text-sm"
                placeholder="Search birds by band ID, species, mutation, cage..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode('grid')}
              className={`relative inline-flex items-center px-4 py-2 rounded-l-md border text-sm font-medium ${
                viewMode === 'grid'
                  ? 'bg-cyan-50 border-cyan-500 text-cyan-700 z-10'
                  : 'bg-white border-gray-300 text-slate-700 hover:bg-slate-50/80'
              }`}
            >
              <Squares2X2Icon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`relative inline-flex items-center px-4 py-2 rounded-r-md border-t border-r border-b text-sm font-medium ${
                viewMode === 'table'
                  ? 'bg-cyan-50 border-cyan-500 text-cyan-700 z-10'
                  : 'bg-white border-gray-300 text-slate-700 hover:bg-slate-50/80'
              }`}
            >
              <TableCellsIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-slate-700 hover:bg-slate-50/80"
          >
            <FunnelIcon className="h-5 w-5 mr-2" />
            Filters
            {Object.keys(filters).length > 0 && (
              <span className="ml-2 bg-cyan-100 text-cyan-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                {Object.keys(filters).length}
              </span>
            )}
          </button>
        </div>

        {/* Expandable Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Gender</label>
                <select
                  value={filters.gender || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, gender: e.target.value as Gender || undefined }))}
                  className="block w-full rounded-md border-gray-300 text-sm focus:border-cyan-500 focus:ring-cyan-500"
                >
                  <option key="empty-option-1753461297356" value="">All Genders</option>
                  <option value={Gender.MALE}>Male</option>
                  <option value={Gender.FEMALE}>Female</option>
                  <option value={Gender.UNKNOWN}>Unknown</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as BirdStatus || undefined }))}
                  className="block w-full rounded-md border-gray-300 text-sm focus:border-cyan-500 focus:ring-cyan-500"
                >
                  <option key="empty-option-1753461297356" value="">All Statuses</option>
                  <option value={BirdStatus.PAIRED}>Paired</option>
                  <option value={BirdStatus.AVAILABLE}>Available</option>
                  <option value={BirdStatus.FOR_SALE}>For Sale</option>
                  <option value={BirdStatus.SOLD}>Sold</option>
                  <option value={BirdStatus.DECEASED}>Deceased</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Species</label>
                <select
                  value={filters.speciesId || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, speciesId: e.target.value || undefined }))}
                  className="block w-full rounded-md border-gray-300 text-sm focus:border-cyan-500 focus:ring-cyan-500"
                >
                  <option key="empty-option-1753461297356" value="">All Species</option>
                  {uniqueSpecies.map((speciesId) => {
                    const species = speciesMap.get(speciesId);
                    return species ? (
                      <option key={speciesId} value={speciesId}>
                        {species.commonName}
                      </option>
                    ) : null;
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cage</label>
                <CageSelect
                  id="filterCage"
                  value={filters.cageNumber || ''}
                  onChange={(cageNumber) => setFilters((prev) => ({ ...prev, cageNumber: cageNumber || undefined }))}
                  allowEmpty
                  allowCreate={false}
                  includeInactive
                  emptyLabel="All cages"
                />
              </div>
            </div>

            {Object.keys(filters).length > 0 && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80"
                >
                  <XMarkIcon className="h-4 w-4 mr-1" />
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-slate-700">
          Showing {paginatedBirds.length} of {filteredBirds.length} birds
        </p>
        
        <div className="flex items-center space-x-2">
          <label className="text-sm text-slate-700">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as keyof Bird)}
            className="rounded-md border-gray-300 text-sm focus:border-cyan-500 focus:ring-cyan-500"
          >
            <option key="bandId-1753461297356" value="bandId">Band ID</option>
            <option key="species-1753461297356" value="species">Species</option>
            <option key="gender-1753461297356" value="gender">Gender</option>
            <option key="status-1753461297356" value="status">Status</option>
            <option key="hatchingDate-1753461297356" value="hatchingDate">Hatching Date</option>
            <option key="cageNumber-1753461297356" value="cageNumber">Cage Number</option>
          </select>
          
          <SortOrderButton
            order={sortOrder}
            onToggle={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          />
        </div>
      </div>

      {/* Content */}
      {viewMode === 'grid' ? (
        // Grid View
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {paginatedBirds.map((bird) => (
            <div key={bird.id} className="bg-white overflow-hidden shadow rounded-lg hover:shadow-lg transition-shadow">
              {/* Bird Image */}
              <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                {bird.avatarPhotoId && bird.photos && bird.photos.length > 0 ? (
                  <Avatar
                    photo={bird.avatarPhotoId}
                    size="xl"
                    shape="rounded"
                    objectFit="cover"
                    objectPosition="center"
                    autoCrop={true}
                    className="w-full h-48"
                    alt={bird.bandId}
                    fallbackText={bird.bandId}
                  />
                ) : bird.photos && bird.photos.length > 0 ? (
                  <Avatar
                    photo={bird.photos[0]}
                    size="xl"
                    shape="rounded"
                    objectFit="cover"
                    objectPosition="center"
                    autoCrop={true}
                    className="w-full h-48"
                    alt={bird.bandId}
                    fallbackText={bird.bandId}
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                    <UserIcon className="h-16 w-16 text-slate-400" />
                  </div>
                )}
              </div>

              {/* Bird Info */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">{bird.bandId}</h3>
                  <span className={`text-xl font-bold ${getGenderColorClass(bird.gender)}`}>
                    {getGenderIcon(bird.gender)}
                  </span>
                </div>
                
                <p className="text-sm text-slate-600 mt-1">
                  {bird.species?.commonName || 'Unknown Species'}
                </p>
                
                {bird.mutationName && (
                  <p className="text-sm text-cyan-600 mt-1">{bird.mutationName}</p>
                )}

                <div className="mt-3 flex items-center justify-between">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(bird.status)}`}>
                    {bird.status}
                  </span>
                  <div className="flex items-center space-x-2">
                    {bird.healthTests && bird.healthTests.length > 0 && (
                      <BirdHealthIndicator 
                        healthTests={bird.healthTests} 
                        size="sm"
                      />
                    )}
                    <span className="text-sm text-slate-500">Cage {bird.cageNumber}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-4 flex space-x-2">
                  <button
                    onClick={() => openDetailsModal(bird)}
                    className="flex-1 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 px-3 py-2 rounded-md text-sm font-medium flex items-center justify-center"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    View
                  </button>
                  <button
                    onClick={() => openEditModal(bird)}
                    className="flex-1 bg-slate-50/80 text-slate-700 hover:bg-gray-100 px-3 py-2 rounded-md text-sm font-medium flex items-center justify-center"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Edit
                  </button>
                  {bird.status === BirdStatus.FOR_SALE && (
                    <button
                      onClick={() => openSellModal(bird)}
                      className="flex-1 bg-green-50 text-green-700 hover:bg-green-100 px-3 py-2 rounded-md text-sm font-medium flex items-center justify-center"
                    >
                      <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                      Sell
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Table View
        <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Bird
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Species
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Gender
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Cage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Health
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedBirds.map((bird) => (
                <tr key={bird.id} className="hover:bg-slate-50/80">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {bird.avatarPhotoId && bird.photos && bird.photos.length > 0 ? (
                        <Avatar
                          photo={bird.avatarPhotoId}
                          size="sm"
                          shape="circle"
                          objectFit="cover"
                          objectPosition="center"
                          autoCrop={true}
                          alt={bird.bandId}
                          fallbackText={bird.bandId}
                        />
                      ) : bird.photos && bird.photos.length > 0 ? (
                        <Avatar
                          photo={bird.photos[0]}
                          size="sm"
                          shape="circle"
                          objectFit="cover"
                          objectPosition="center"
                          autoCrop={true}
                          alt={bird.bandId}
                          fallbackText={bird.bandId}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <UserIcon className="h-6 w-6 text-slate-400" />
                        </div>
                      )}
                      <div className="ml-4">
                        <div className="text-sm font-medium text-slate-900">{bird.bandId}</div>
                        {bird.mutationName && (
                          <div className="text-sm text-cyan-600">{bird.mutationName}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-900">{bird.species?.commonName || 'Unknown'}</div>
                    <div className="text-sm text-slate-500">{bird.species?.genus} {bird.species?.species}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getGenderColorClass(bird.gender)}`}>
                      {getGenderIcon(bird.gender)} {bird.gender}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(bird.status)}`}>
                      {bird.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {bird.cageNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <BirdHealthIndicator 
                      healthTests={bird.healthTests || []} 
                      size="sm"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openDetailsModal(bird)}
                      className="text-cyan-600 hover:text-indigo-900 mr-3"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => openEditModal(bird)}
                      className="text-slate-600 hover:text-slate-900 mr-3"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    {bird.status === BirdStatus.FOR_SALE && (
                      <button
                        onClick={() => openSellModal(bird)}
                        className="text-green-600 hover:text-green-900"
                        title="Sell Bird"
                      >
                        <CurrencyDollarIcon className="h-5 w-5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                Showing{' '}
                <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, filteredBirds.length)}
                </span>{' '}
                of <span className="font-medium">{filteredBirds.length}</span> results
              </p>
            </div>
            
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                          page === currentPage
                            ? 'z-10 bg-cyan-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600'
                            : 'text-slate-900 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return (
                      <span
                        key={page}
                        className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0"
                      >
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredBirds.length === 0 && (
        <EmptyState
          title="No birds found"
          description={
            searchTerm || Object.keys(filters).length > 0
              ? 'Try adjusting your search or filters.'
              : 'Get started by adding your first bird.'
          }
          icon={<UserIcon className="h-6 w-6" />}
          action={
            !searchTerm && Object.keys(filters).length === 0 ? (
              <button type="button" onClick={openAddBird} className="btn btn-primary focus-pop">
                <PlusIcon className="h-5 w-5" />
                Add Bird
              </button>
            ) : undefined
          }
        />
      )}

      {/* Modals */}
      <ImportBirdCertificateModal
        open={showImportCertificateModal}
        onClose={() => setShowImportCertificateModal(false)}
        onImport={handleCertificateImport}
      />

      <AddBirdModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setAddBirdDraft(null);
        }}
        onAdd={handleAddBird}
        initialDraft={addBirdDraft}
      />

      {selectedBird && (
        <>
          <EditBirdModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setSelectedBird(null);
            }}
            onEdit={handleEditBird}
            onBirdUpdate={(updatedBird) => {
              setBirds((prev) => prev.map((b) => (b.id === updatedBird.id ? updatedBird : b)));
              setSelectedBird(updatedBird);
            }}
            bird={selectedBird}
          />

          <SellBirdModal
            isOpen={showSellModal}
            onClose={() => {
              setShowSellModal(false);
              setSelectedBird(null);
            }}
            bird={selectedBird}
            onSaleComplete={() => {
              loadBirds(); // Reload birds to update status
              setShowSellModal(false);
              setSelectedBird(null);
            }}
          />

          {/* Bird Details Modal */}
          <BrandModal
            open={showDetailsModal}
            onClose={() => setShowDetailsModal(false)}
            maxWidthClass="max-w-6xl"
          >
            <BrandModalHeader
              title={`${selectedBird.bandId} - ${selectedBird.species?.commonName}`}
              onClose={() => setShowDetailsModal(false)}
            />
            <BrandModalBody>
              <BirdDetails
                bird={selectedBird}
                onClose={() => setShowDetailsModal(false)}
                onUpdate={(updatedBird) => {
                  setBirds(prev => prev.map(b => b.id === updatedBird.id ? updatedBird : b));
                  setSelectedBird(updatedBird);
                }}
              />
            </BrandModalBody>
          </BrandModal>
        </>
      )}
    </div>
  );
} 